// src/pages/fiscal/PaymentHistoryPage.tsx
import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  Calendar,
  Download,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  User,
  Users,
  AlertTriangle,
  ExternalLink,
  Package
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { userPaymentService, ContractorYearEndSummary } from '../../services/userPayment.service';
import { companySettingsService } from '../../services/companySettings.service';
import { UserPayment, CompanySettings } from '../../types/finance.types';
import { userService } from '../../services/user.service';
import { User as UserType } from '../../types/user.types';
import { format, startOfYear, endOfYear, getYear } from 'date-fns';
import { toast } from 'react-hot-toast';
import { formatSafeDate } from '../../utils/dateUtils';

const PaymentHistoryPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const [payments, setPayments] = useState<UserPayment[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(getYear(new Date()));
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [generating1099, setGenerating1099] = useState(false);

  // Year-end 1099 package state
  const [activeTab, setActiveTab] = useState<'history' | 'yearend'>('history');
  const [yearEndSummaries, setYearEndSummaries] = useState<ContractorYearEndSummary[]>([]);
  const [loadingYearEnd, setLoadingYearEnd] = useState(false);
  const [generatingPackage, setGeneratingPackage] = useState(false);

  const isAdmin = userProfile?.role === 'provider_admin';
  const currentYear = getYear(new Date());
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    loadData();
  }, [currentUser, userProfile, selectedYear, selectedUser]);

  useEffect(() => {
    if (activeTab === 'yearend' && isAdmin) {
      loadYearEndData();
    }
  }, [activeTab, selectedYear, isAdmin]);

  const loadData = async () => {
    if (!currentUser || !userProfile) return;

    setLoading(true);
    try {
      // Load users for admin - provider_admin sees all users
      if (isAdmin) {
        const userList = await userService.getAllUsers();
        setUsers(userList.filter(u => u.role === 'field'));
      }

      // Load payments
      const yearStart = startOfYear(new Date(selectedYear, 0, 1));
      const yearEnd = endOfYear(new Date(selectedYear, 0, 1));

      let paymentList: UserPayment[];
      if (isAdmin && selectedUser) {
        paymentList = await userPaymentService.getUserPayments({ userId: selectedUser });
      } else if (!isAdmin) {
        paymentList = await userPaymentService.getUserPayments({ userId: currentUser.uid });
      } else {
        // Admin with no user selected - get all payments
        paymentList = await userPaymentService.getAllPayments();
      }

      // Filter by year
      const filteredPayments = paymentList.filter(p => {
        const paymentDate = p.paidDate?.toDate() || p.createdAt?.toDate();
        return paymentDate && paymentDate >= yearStart && paymentDate <= yearEnd;
      });

      setPayments(filteredPayments);
    } catch (error) {
      console.error('Error loading payment data:', error);
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const loadYearEndData = async () => {
    if (!isAdmin) return;

    setLoadingYearEnd(true);
    try {
      const summaries = await userPaymentService.generateYearEnd1099Package(selectedYear);
      setYearEndSummaries(summaries);
    } catch (error) {
      console.error('Error loading year-end data:', error);
      toast.error('Failed to load year-end contractor data');
    } finally {
      setLoadingYearEnd(false);
    }
  };

  const calculateStats = () => {
    const paid = payments.filter(p => p.status === 'paid');
    const pending = payments.filter(p => p.status === 'pending');

    return {
      totalEarnings: paid.reduce((sum, p) => sum + (p.amount || 0), 0),
      pendingAmount: pending.reduce((sum, p) => sum + (p.amount || 0), 0),
      totalPayments: paid.length,
      pendingPayments: pending.length
    };
  };

  const stats = calculateStats();

  const generate1099 = async (userId: string, userName: string) => {
    setGenerating1099(true);
    try {
      // For now, we'll use jsPDF to generate a simple 1099-NEC form
      const { jsPDF } = await import('jspdf');

      const doc = new jsPDF();

      // Header
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Form 1099-NEC', 105, 20, { align: 'center' });

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Nonemployee Compensation', 105, 28, { align: 'center' });
      doc.text(`Tax Year ${selectedYear}`, 105, 36, { align: 'center' });

      // Payer Information
      doc.setFontSize(10);
      doc.text("PAYER'S name, street address, city or town, state or province, country, ZIP or foreign postal code", 20, 55);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(userProfile?.organizationName || 'Organization Name', 20, 62);

      // Recipient Information
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text("RECIPIENT'S name", 20, 85);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(userName, 20, 92);

      // Box 1 - Nonemployee Compensation
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.rect(20, 110, 80, 30);
      doc.text('1. Nonemployee compensation', 22, 117);

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`$${stats.totalEarnings.toFixed(2)}`, 22, 130);

      // Notes
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('This is a generated 1099-NEC form for informational purposes.', 20, 160);
      doc.text(`Generated on ${format(new Date(), 'MMMM d, yyyy')}`, 20, 168);

      // Download
      doc.save(`1099-NEC_${selectedYear}_${userName.replace(/\s+/g, '_')}.pdf`);

      toast.success('1099-NEC generated successfully');
    } catch (error) {
      console.error('Error generating 1099:', error);
      toast.error('Failed to generate 1099');
    } finally {
      setGenerating1099(false);
    }
  };

  const generateYearEnd1099Package = async () => {
    setGeneratingPackage(true);
    try {
      const { jsPDF } = await import('jspdf');

      // Get company settings
      let companySettings: CompanySettings | null = null;
      try {
        companySettings = await companySettingsService.getCompanySettings();
      } catch (e) {
        console.warn('Could not load company settings:', e);
      }

      const companyName = companySettings?.companyName || userProfile?.organizationName || 'Company';
      const companyAddress = companySettings?.companyAddress || '';
      const companyCity = companySettings?.companyCity || '';
      const companyState = companySettings?.companyState || '';
      const companyZip = companySettings?.companyZip || '';

      const doc = new jsPDF();
      let pageNumber = 1;

      // Filter contractors requiring 1099 (>= $600)
      const contractorsRequiring1099 = yearEndSummaries.filter(s => s.requires1099);

      if (contractorsRequiring1099.length === 0) {
        toast.error('No contractors meet the $600 threshold for 1099 reporting');
        setGeneratingPackage(false);
        return;
      }

      // ==========================================
      // Cover Page
      // ==========================================
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text('1099 Summary Package', 105, 60, { align: 'center' });

      doc.setFontSize(18);
      doc.setFont('helvetica', 'normal');
      doc.text(`Tax Year ${selectedYear}`, 105, 75, { align: 'center' });

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(companyName, 105, 100, { align: 'center' });

      if (companyAddress) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.text(companyAddress, 105, 110, { align: 'center' });
        if (companyCity || companyState || companyZip) {
          doc.text(`${companyCity}${companyCity && companyState ? ', ' : ''}${companyState} ${companyZip}`, 105, 118, { align: 'center' });
        }
      }

      // Summary Stats Box
      doc.setFillColor(245, 247, 250);
      doc.roundedRect(30, 140, 150, 60, 5, 5, 'F');

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text('Summary', 40, 155);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Contractors: ${yearEndSummaries.length}`, 40, 168);
      doc.text(`Contractors Requiring 1099 (>=$600): ${contractorsRequiring1099.length}`, 40, 178);
      doc.text(`W9s On File: ${yearEndSummaries.filter(s => s.w9Status === 'uploaded').length}`, 40, 188);
      doc.text(`W9s Missing: ${yearEndSummaries.filter(s => s.w9Status === 'missing' && s.requires1099).length}`, 40, 198);

      const totalPaid = yearEndSummaries.reduce((sum, s) => sum + s.totalPaid, 0);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Amount Paid: $${totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 40, 212);

      // Generation info
      doc.setTextColor(120, 120, 120);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}`, 105, 270, { align: 'center' });

      doc.setTextColor(0, 0, 0);

      // ==========================================
      // Individual Contractor Pages
      // ==========================================
      for (const contractor of contractorsRequiring1099) {
        doc.addPage();
        pageNumber++;

        // Header
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Contractor Payment Summary', 20, 25);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Tax Year ${selectedYear}`, 20, 33);
        doc.setTextColor(0, 0, 0);

        // Contractor Info Box
        doc.setDrawColor(200, 200, 200);
        doc.setFillColor(250, 250, 252);
        doc.roundedRect(20, 45, 170, 50, 3, 3, 'FD');

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(contractor.user.name || 'Unknown', 30, 60);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Email: ${contractor.user.email}`, 30, 72);

        if (contractor.user.phoneNumber) {
          doc.text(`Phone: ${contractor.user.phoneNumber}`, 30, 82);
        }

        // W9 Status
        const w9StatusX = 130;
        if (contractor.w9Status === 'uploaded') {
          doc.setFillColor(220, 252, 231);
          doc.roundedRect(w9StatusX, 55, 50, 18, 3, 3, 'F');
          doc.setTextColor(22, 101, 52);
          doc.setFont('helvetica', 'bold');
          doc.text('W9 On File', w9StatusX + 25, 66, { align: 'center' });
        } else {
          doc.setFillColor(254, 226, 226);
          doc.roundedRect(w9StatusX, 55, 50, 18, 3, 3, 'F');
          doc.setTextColor(153, 27, 27);
          doc.setFont('helvetica', 'bold');
          doc.text('W9 Missing', w9StatusX + 25, 66, { align: 'center' });
        }
        doc.setTextColor(0, 0, 0);

        // Payment Details
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('Payment Details', 20, 115);

        doc.setDrawColor(220, 220, 220);
        doc.line(20, 120, 190, 120);

        // Payment table
        const tableStartY = 130;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        // Headers
        doc.setFillColor(245, 247, 250);
        doc.rect(20, tableStartY - 5, 170, 12, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('Payment Type', 25, tableStartY + 3);
        doc.text('Amount', 160, tableStartY + 3, { align: 'right' });

        // Data rows
        doc.setFont('helvetica', 'normal');
        let rowY = tableStartY + 18;

        if (contractor.userPaymentsTotal > 0) {
          doc.text('Staff Payments', 25, rowY);
          doc.text(`$${contractor.userPaymentsTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 160, rowY, { align: 'right' });
          rowY += 12;
        }

        if (contractor.fieldPaymentsTotal > 0) {
          doc.text('Field Payments (Weekly + Holdback)', 25, rowY);
          doc.text(`$${contractor.fieldPaymentsTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 160, rowY, { align: 'right' });
          rowY += 12;
        }

        // Total
        doc.setDrawColor(200, 200, 200);
        doc.line(20, rowY, 190, rowY);
        rowY += 10;
        doc.setFont('helvetica', 'bold');
        doc.text('Total Paid', 25, rowY);
        doc.setFontSize(12);
        doc.text(`$${contractor.totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 160, rowY, { align: 'right' });

        // 1099 Notice
        if (contractor.requires1099) {
          const noticeY = rowY + 30;
          doc.setFillColor(254, 243, 199);
          doc.roundedRect(20, noticeY, 170, 25, 3, 3, 'F');
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(146, 64, 14);
          doc.text('1099-NEC Required', 30, noticeY + 10);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.text('Total payments exceed $600 threshold. A 1099-NEC form is required for this contractor.', 30, noticeY + 19);
          doc.setTextColor(0, 0, 0);
        }

        // Page number
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${pageNumber}`, 105, 285, { align: 'center' });
        doc.setTextColor(0, 0, 0);
      }

      // ==========================================
      // Summary Table Page
      // ==========================================
      doc.addPage();
      pageNumber++;

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('All Contractors Summary', 20, 25);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Tax Year ${selectedYear}`, 20, 33);
      doc.setTextColor(0, 0, 0);

      // Table headers
      const tableY = 50;
      doc.setFillColor(245, 247, 250);
      doc.rect(15, tableY - 5, 180, 12, 'F');

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Name', 20, tableY + 3);
      doc.text('Email', 70, tableY + 3);
      doc.text('W9', 125, tableY + 3);
      doc.text('Total Paid', 150, tableY + 3);
      doc.text('1099?', 175, tableY + 3);

      // Table rows
      doc.setFont('helvetica', 'normal');
      let currentY = tableY + 18;
      const rowHeight = 10;
      const pageHeight = 280;

      for (const contractor of yearEndSummaries) {
        if (currentY > pageHeight - 20) {
          // Add new page
          doc.addPage();
          pageNumber++;
          currentY = 30;

          // Re-add headers
          doc.setFillColor(245, 247, 250);
          doc.rect(15, currentY - 5, 180, 12, 'F');
          doc.setFont('helvetica', 'bold');
          doc.text('Name', 20, currentY + 3);
          doc.text('Email', 70, currentY + 3);
          doc.text('W9', 125, currentY + 3);
          doc.text('Total Paid', 150, currentY + 3);
          doc.text('1099?', 175, currentY + 3);
          doc.setFont('helvetica', 'normal');
          currentY += 18;
        }

        // Alternate row background
        if (yearEndSummaries.indexOf(contractor) % 2 === 0) {
          doc.setFillColor(252, 252, 253);
          doc.rect(15, currentY - 5, 180, rowHeight, 'F');
        }

        const name = contractor.user.name || 'Unknown';
        const displayName = name.length > 20 ? name.substring(0, 18) + '...' : name;
        doc.text(displayName, 20, currentY);

        const email = contractor.user.email;
        const displayEmail = email.length > 25 ? email.substring(0, 23) + '...' : email;
        doc.text(displayEmail, 70, currentY);

        // W9 Status with color
        if (contractor.w9Status === 'uploaded') {
          doc.setTextColor(22, 101, 52);
          doc.text('Yes', 125, currentY);
        } else {
          doc.setTextColor(153, 27, 27);
          doc.text('No', 125, currentY);
        }
        doc.setTextColor(0, 0, 0);

        doc.text(`$${contractor.totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 150, currentY);

        if (contractor.requires1099) {
          doc.setTextColor(146, 64, 14);
          doc.text('Yes', 175, currentY);
          doc.setTextColor(0, 0, 0);
        } else {
          doc.text('No', 175, currentY);
        }

        currentY += rowHeight;
      }

      // Total row
      currentY += 5;
      doc.setDrawColor(200, 200, 200);
      doc.line(15, currentY, 195, currentY);
      currentY += 8;
      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL', 20, currentY);
      doc.text(`$${totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 150, currentY);

      // Page number
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${pageNumber}`, 105, 285, { align: 'center' });

      // Download the PDF
      doc.save(`1099_Package_${selectedYear}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);

      toast.success('1099 Package generated successfully!');
    } catch (error) {
      console.error('Error generating 1099 package:', error);
      toast.error('Failed to generate 1099 package');
    } finally {
      setGeneratingPackage(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <span className="flex items-center gap-1 px-2.5 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium">
            <CheckCircle className="h-3 w-3" />
            Paid
          </span>
        );
      case 'pending':
        return (
          <span className="flex items-center gap-1 px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium">
            <Clock className="h-3 w-3" />
            Pending
          </span>
        );
      case 'held':
        return (
          <span className="flex items-center gap-1 px-2.5 py-1 bg-coral-100 text-coral-600 rounded-full text-xs font-medium">
            <AlertCircle className="h-3 w-3" />
            Held
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Payment History</h1>
          <p className="text-gray-500 mt-1">View earnings and generate 1099 forms</p>
        </div>
        {activeTab === 'history' && stats.totalEarnings >= 600 && (
          <button
            onClick={() => generate1099(selectedUser || currentUser!.uid, selectedUser ? users.find(u => u.uid === selectedUser)?.name || '' : userProfile?.name || '')}
            disabled={generating1099}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-200 disabled:opacity-50"
          >
            {generating1099 ? (
              <>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                Generating...
              </>
            ) : (
              <>
                <Download className="h-5 w-5" />
                Generate 1099-NEC
              </>
            )}
          </button>
        )}
      </div>

      {/* Tabs for Admin */}
      {isAdmin && (
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Payment History
            </div>
          </button>
          <button
            onClick={() => setActiveTab('yearend')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'yearend'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Year-End 1099 Package
            </div>
          </button>
        </div>
      )}

      {/* Year Selector (shared between tabs) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          {isAdmin && activeTab === 'history' && (
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Users</option>
                {users.map(user => (
                  <option key={user.uid} value={user.uid}>{user.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Payment History Tab Content */}
      {activeTab === 'history' && (
        <>
          {/* Stats */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-accent-400 to-accent-600 rounded-xl p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <DollarSign className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-white/80">Total Earnings</p>
                  <p className="text-xl font-bold">${stats.totalEarnings.toFixed(2)}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-xl p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-white/80">Pending</p>
                  <p className="text-xl font-bold">${stats.pendingAmount.toFixed(2)}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <CheckCircle className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-white/80">Payments</p>
                  <p className="text-xl font-bold">{stats.totalPayments}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-coral-300 to-coral-500 rounded-xl p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-white/80">Avg Payment</p>
                  <p className="text-xl font-bold">
                    ${stats.totalPayments > 0 ? (stats.totalEarnings / stats.totalPayments).toFixed(2) : '0.00'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 1099 Notice */}
          {stats.totalEarnings >= 600 && (
            <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-primary-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-primary-800">1099-NEC Available</h4>
                  <p className="text-sm text-primary-600 mt-1">
                    Your earnings of ${stats.totalEarnings.toFixed(2)} for {selectedYear} exceed the $600 threshold.
                    You can download your 1099-NEC form for tax purposes.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Payments List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">Payment History</h3>
            </div>

            {payments.length === 0 ? (
              <div className="p-12 text-center">
                <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-800 mb-2">No payments found</h3>
                <p className="text-gray-500">No payments recorded for {selectedYear}.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-accent-100 rounded-lg">
                          <DollarSign className="h-5 w-5 text-accent-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-800">
                              ${(payment.amount || 0).toFixed(2)}
                            </span>
                            {getStatusBadge(payment.status)}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                            {isAdmin && payment.userName && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {payment.userName}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {payment.paidDate
                                ? formatSafeDate(payment.paidDate, 'MMM d, yyyy')
                                : 'Pending'}
                            </span>
                            {payment.metrics?.leadsGenerated && (
                              <span className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {payment.metrics.leadsGenerated} leads
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="text-sm text-gray-500">
                          {payment.projectName || 'Multiple Projects'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Year-End 1099 Package Tab Content */}
      {activeTab === 'yearend' && isAdmin && (
        <>
          {loadingYearEnd ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading contractor data...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl p-4 text-white">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Users className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm text-white/80">Total Contractors</p>
                      <p className="text-xl font-bold">{yearEndSummaries.length}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl p-4 text-white">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm text-white/80">Require 1099</p>
                      <p className="text-xl font-bold">{yearEndSummaries.filter(s => s.requires1099).length}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-4 text-white">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <CheckCircle className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm text-white/80">W9s On File</p>
                      <p className="text-xl font-bold">{yearEndSummaries.filter(s => s.w9Status === 'uploaded').length}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-red-400 to-red-600 rounded-xl p-4 text-white">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <AlertTriangle className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm text-white/80">Missing W9s</p>
                      <p className="text-xl font-bold">
                        {yearEndSummaries.filter(s => s.w9Status === 'missing' && s.requires1099).length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Generate Package Button */}
              <div className="flex justify-end mb-6">
                <button
                  onClick={generateYearEnd1099Package}
                  disabled={generatingPackage || yearEndSummaries.filter(s => s.requires1099).length === 0}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-200 disabled:opacity-50"
                >
                  {generatingPackage ? (
                    <>
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                      Generating Package...
                    </>
                  ) : (
                    <>
                      <Package className="h-5 w-5" />
                      Generate 1099 Package
                    </>
                  )}
                </button>
              </div>

              {/* Missing W9 Warning */}
              {yearEndSummaries.filter(s => s.w9Status === 'missing' && s.requires1099).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">Missing W9 Documents</h4>
                      <p className="text-sm text-amber-600 mt-1">
                        {yearEndSummaries.filter(s => s.w9Status === 'missing' && s.requires1099).length} contractor(s)
                        requiring 1099s do not have a W9 on file. Please collect W9 forms before issuing 1099-NEC forms.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Contractors Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-800">Contractor Payment Summary - {selectedYear}</h3>
                </div>

                {yearEndSummaries.length === 0 ? (
                  <div className="p-12 text-center">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-800 mb-2">No contractor payments found</h3>
                    <p className="text-gray-500">No payments were made to contractors in {selectedYear}.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                            Contractor
                          </th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                            Email
                          </th>
                          <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                            W9 Status
                          </th>
                          <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                            Total Paid
                          </th>
                          <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                            1099 Required
                          </th>
                          <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {yearEndSummaries.map((summary) => (
                          <tr key={summary.user.uid} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                                  <User className="h-4 w-4 text-primary-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-800">{summary.user.name}</p>
                                  {summary.user.phoneNumber && (
                                    <p className="text-xs text-gray-500">{summary.user.phoneNumber}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {summary.user.email}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {summary.w9Status === 'uploaded' ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                  <CheckCircle className="h-3 w-3" />
                                  On File
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                  <AlertTriangle className="h-3 w-3" />
                                  Missing
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <p className="font-semibold text-gray-800">
                                ${summary.totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                              <div className="text-xs text-gray-500">
                                {summary.userPaymentsTotal > 0 && (
                                  <span>Staff: ${summary.userPaymentsTotal.toFixed(2)}</span>
                                )}
                                {summary.userPaymentsTotal > 0 && summary.fieldPaymentsTotal > 0 && ' | '}
                                {summary.fieldPaymentsTotal > 0 && (
                                  <span>Field: ${summary.fieldPaymentsTotal.toFixed(2)}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {summary.requires1099 ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                  Yes
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">No</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {summary.w9Url && (
                                <a
                                  href={summary.w9Url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                  View W9
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 font-semibold">
                          <td className="px-4 py-3 text-gray-800" colSpan={3}>
                            Total
                          </td>
                          <td className="px-4 py-3 text-right text-gray-800">
                            ${yearEndSummaries.reduce((sum, s) => sum + s.totalPaid, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PaymentHistoryPage;
